import type { PopoverProps } from '@mantine/core';
import { Popover, createStyles } from '@mantine/core';
import {
	useCallback,
	useRef,
	useState,
	type PropsWithChildren,
	type ReactNode,
} from 'react';

const useStyles = createStyles((theme) => ({
	dropdown: {
		boxShadow: '0px 6px 16px 0px rgba(0, 0, 0, 0.12)',
		border: `${theme.other.borderWidth.xs}px solid ${theme.other.getColor('border/primary/default')}`,
	},
}));

interface HoverCardProps {
	target: ReactNode;
	position?: PopoverProps['position'];
	hidden?: boolean;
	withinPortal?: boolean;
	initialOpen?: boolean;
	targetFullWidth?: boolean;
	openDelay?: number;
}

export function HoverCard({
	target,
	children,
	position,
	hidden = false,
	initialOpen = false,
	withinPortal,
	targetFullWidth,
	openDelay = 0,
	...other
}: PropsWithChildren<HoverCardProps> & Partial<PopoverProps>) {
	const { classes } = useStyles();
	const [open, setOpen] = useState(initialOpen);
	const openTimeoutRef = useRef<number | null>(null);

	const handleMouseEnter = useCallback(() => {
		if (openTimeoutRef.current !== null) {
			window.clearTimeout(openTimeoutRef.current);
		}
		if (openDelay > 0) {
			const timeoutId = window.setTimeout(() => {
				if (openTimeoutRef.current === timeoutId) {
					setOpen(true);
					openTimeoutRef.current = null;
				}
			}, openDelay);
			openTimeoutRef.current = timeoutId;
		} else {
			setOpen(true);
		}
	}, [openDelay]);

	const handleMouseLeave = useCallback(() => {
		if (openTimeoutRef.current !== null) {
			window.clearTimeout(openTimeoutRef.current);
			openTimeoutRef.current = null;
		}
		setOpen(false);
	}, []);

	return (
		<Popover
			withinPortal={withinPortal}
			opened={open}
			classNames={classes}
			position={position}
			{...other}
		>
			<Popover.Target>
				<div
					style={
						targetFullWidth ? { width: '100%', display: 'block' } : undefined
					}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onClick={!hidden ? handleMouseLeave : undefined}
				>
					{target}
				</div>
			</Popover.Target>
			{!hidden && (
				<Popover.Dropdown
					p={0}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{children}
				</Popover.Dropdown>
			)}
		</Popover>
	);
}
